/* =========================================================================
   MORENA — theme.js
   Vanilla JS. No frameworks. Alpine.js is loaded separately for small
   declarative bits (accordions / toggles) but every critical path here is
   framework-free so the cart still works if the CDN is slow.
   ========================================================================= */
(function () {
  'use strict';

  var Morena = window.Morena || {};
  window.Morena = Morena;
  var routes = Morena.routes || {};
  var strings = Morena.strings || {};

  /* ----- tiny helpers ---------------------------------------------------- */
  function $(sel, ctx) { return (ctx || document).querySelector(sel); }
  function $all(sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); }
  function on(el, ev, fn, opts) { if (el) el.addEventListener(ev, fn, opts); }
  function formatMoney(cents) {
    var amount = (cents / 100);
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: (Morena.currency || 'USD')
      }).format(amount);
    } catch (e) {
      return '$' + amount.toFixed(2);
    }
  }

  /* =======================================================================
     CART STORE — single source of truth, talks to Shopify AJAX API
     ===================================================================== */
  var Cart = {
    listeners: [],
    subscribe: function (fn) { this.listeners.push(fn); },
    emit: function (state) { this.listeners.forEach(function (fn) { fn(state); }); },

    fetch: function () {
      return fetch(routes.cart_url + '.js', { headers: { 'Accept': 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (state) { Cart.emit(state); return state; });
    },

    add: function (items) {
      return fetch(routes.cart_add_url + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: items })
      }).then(function (r) {
        if (!r.ok) return r.json().then(function (e) { throw e; });
        return r.json();
      }).then(function () { return Cart.fetch(); });
    },

    change: function (id, quantity) {
      return fetch(routes.cart_change_url + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: id, quantity: quantity })
      }).then(function (r) { return r.json(); })
        .then(function (state) { Cart.emit(state); return state; });
    }
  };
  Morena.Cart = Cart;

  /* =======================================================================
     OVERLAY / SCROLL LOCK
     ===================================================================== */
  function lockScroll(lock) {
    document.documentElement.classList.toggle('no-scroll', lock);
    document.body.classList.toggle('no-scroll', lock);
  }

  /* =======================================================================
     DRAWER CONTROLLER (cart drawer + nav drawer share behaviour)
     ===================================================================== */
  function Drawer(el) {
    if (!el) return null;
    var overlay = $('[data-overlay]');
    var lastFocused = null;

    function focusables() {
      return $all('a[href], button:not([disabled]), input, [tabindex]:not([tabindex="-1"])', el);
    }
    function trap(e) {
      if (e.key === 'Escape') { close(); return; }
      if (e.key !== 'Tab') return;
      var f = focusables();
      if (!f.length) return;
      var first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
    function open() {
      lastFocused = document.activeElement;
      el.setAttribute('data-open', 'true');
      el.setAttribute('aria-hidden', 'false');
      if (overlay) overlay.setAttribute('data-open', 'true');
      lockScroll(true);
      document.addEventListener('keydown', trap);
      var first = focusables()[0];
      if (first) first.focus();
    }
    function close() {
      el.setAttribute('data-open', 'false');
      el.setAttribute('aria-hidden', 'true');
      if (overlay && !$('[data-drawer][data-open="true"]')) overlay.setAttribute('data-open', 'false');
      lockScroll(false);
      document.removeEventListener('keydown', trap);
      if (lastFocused) lastFocused.focus();
    }
    return { open: open, close: close, el: el };
  }

  /* =======================================================================
     CART DRAWER UI
     ===================================================================== */
  function initCartDrawer() {
    var drawerEl = $('[data-cart-drawer]');
    if (!drawerEl) return;
    var drawer = Drawer(drawerEl);
    Morena.cartDrawer = drawer;

    var itemsEl = $('[data-cart-items]', drawerEl);
    var emptyEl = $('[data-cart-empty]', drawerEl);
    var footEl = $('[data-cart-foot]', drawerEl);
    var subtotalEl = $('[data-cart-subtotal]', drawerEl);

    function render(state) {
      // Update cart counts everywhere (always visible, including 0)
      $all('[data-cart-count]').forEach(function (node) {
        node.textContent = state.item_count;
      });

      var hasItems = state.item_count > 0;
      if (emptyEl) emptyEl.hidden = hasItems;
      if (footEl) footEl.hidden = !hasItems;
      if (itemsEl) itemsEl.hidden = !hasItems;

      if (subtotalEl) subtotalEl.textContent = formatMoney(state.total_price);

      if (!itemsEl || !hasItems) { if (itemsEl) itemsEl.innerHTML = ''; return; }

      itemsEl.innerHTML = state.items.map(function (item) {
        var img = item.image
          ? '<img src="' + item.image.replace(/(\.[a-z]+)(\?|$)/i, '_160x$1$2') + '" alt="' + escapeHtml(item.product_title) + '" loading="lazy" width="72" height="90">'
          : '';
        var variant = item.variant_title && item.variant_title !== 'Default Title'
          ? '<span class="line-item__variant">' + escapeHtml(item.variant_title) + '</span>' : '';
        return '' +
          '<div class="line-item" data-line-item data-key="' + item.key + '">' +
            '<a class="line-item__media" href="' + item.url + '">' + img + '</a>' +
            '<div class="line-item__info">' +
              '<a class="line-item__title" href="' + item.url + '">' + escapeHtml(item.product_title) + '</a>' +
              variant +
              '<div class="line-item__bottom">' +
                '<div class="cart-qty" data-qty>' +
                  '<button type="button" data-qty-down aria-label="Decrease quantity">&minus;</button>' +
                  '<span data-qty-value>' + item.quantity + '</span>' +
                  '<button type="button" data-qty-up aria-label="Increase quantity">+</button>' +
                '</div>' +
                '<button type="button" class="line-item__remove" data-remove>Remove</button>' +
              '</div>' +
            '</div>' +
            '<div class="line-item__price">' + formatMoney(item.final_line_price) + '</div>' +
          '</div>';
      }).join('');
    }

    // Event delegation for qty + remove inside the drawer
    on(drawerEl, 'click', function (e) {
      var lineEl = e.target.closest('[data-line-item]');
      if (!lineEl) return;
      var key = lineEl.getAttribute('data-key');
      var valueEl = $('[data-qty-value]', lineEl);
      var current = parseInt(valueEl.textContent, 10);

      if (e.target.closest('[data-remove]')) { Cart.change(key, 0); }
      else if (e.target.closest('[data-qty-up]')) { Cart.change(key, current + 1); }
      else if (e.target.closest('[data-qty-down]')) { Cart.change(key, Math.max(0, current - 1)); }
    });

    // Open triggers — open immediately, refresh cart contents in the background
    // so the drawer always opens even if the cart request is slow or fails.
    $all('[data-cart-toggle]').forEach(function (btn) {
      on(btn, 'click', function (e) { e.preventDefault(); drawer.open(); Cart.fetch(); });
    });
    $all('[data-cart-close]').forEach(function (btn) { on(btn, 'click', drawer.close); });
    on($('[data-overlay]'), 'click', function () { drawer.close(); var nav = Morena.navDrawer; if (nav) nav.close(); });

    Cart.subscribe(render);
    Cart.fetch();
  }

  function escapeHtml(str) {
    return String(str).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  /* =======================================================================
     NAV DRAWER (mobile)
     ===================================================================== */
  function initNavDrawer() {
    var navEl = $('[data-nav-drawer]');
    if (!navEl) return;
    var nav = Drawer(navEl);
    Morena.navDrawer = nav;
    $all('[data-nav-toggle]').forEach(function (b) { on(b, 'click', nav.open); });
    $all('[data-nav-close]').forEach(function (b) { on(b, 'click', nav.close); });
  }

  /* =======================================================================
     PRODUCT FORM — add to cart + variant selection
     ===================================================================== */
  function initProductForms() {
    $all('[data-product-form]').forEach(function (form) {
      var dataEl = $('[data-variants-json]', form.closest('[data-product-root]') || document);
      var variants = dataEl ? JSON.parse(dataEl.textContent) : [];
      var idInput = $('[data-variant-id]', form);
      var submitBtn = $('[data-atc]', form);
      var priceTarget = $('[data-product-price]');
      var root = form.closest('[data-product-root]') || document;

      function currentSelections() {
        return $all('[data-option-input]:checked', root).map(function (i) { return i.value; });
      }
      function matchVariant() {
        var optionInputs = $all('[data-option-input]', root);
        if (!optionInputs.length) {
          return variants[0];
        }
        var sel = currentSelections();
        return variants.find(function (v) {
          return v.options.length === sel.length && v.options.every(function (opt, i) { return opt === sel[i]; });
        });
      }
      function updateVariant() {
        var variant = matchVariant();
        if (!variant) {
          if (submitBtn) { submitBtn.disabled = true; submitBtn.querySelector('[data-atc-text]').textContent = strings.soldOut || 'Unavailable'; }
          return;
        }
        if (idInput) idInput.value = variant.id;
        // Reflect in URL for shareability
        if (history.replaceState) {
          var url = new URL(window.location.href);
          url.searchParams.set('variant', variant.id);
          history.replaceState({}, '', url);
        }
        if (submitBtn) {
          var txt = submitBtn.querySelector('[data-atc-text]');
          submitBtn.disabled = !variant.available;
          if (txt) txt.textContent = variant.available ? (strings.addToCart || 'Add to cart') : (strings.soldOut || 'Sold out');
        }
        if (priceTarget && variant.price_html) priceTarget.innerHTML = variant.price_html;
        // sticky bar price
        var stickyPrice = $('[data-sticky-price]');
        if (stickyPrice && variant.price_formatted) stickyPrice.textContent = variant.price_formatted;
        // toggle availability on option inputs (basic)
        $all('[data-option-input]', root).forEach(function (input) { input.disabled = false; });
      }

      $all('[data-option-input]', root).forEach(function (input) { on(input, 'change', updateVariant); });

      on(form, 'submit', function (e) {
        e.preventDefault();
        if (!idInput || !idInput.value) return;
        var qtyInput = $('[data-quantity]', form);
        var qty = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;
        var txt = submitBtn ? submitBtn.querySelector('[data-atc-text]') : null;
        var original = txt ? txt.textContent : '';
        if (submitBtn) submitBtn.disabled = true;
        if (txt) txt.textContent = '…';

        Cart.add([{ id: parseInt(idInput.value, 10), quantity: qty }])
          .then(function () {
            if (Morena.cartDrawer) Morena.cartDrawer.open();
            if (txt) txt.textContent = original;
            if (submitBtn) submitBtn.disabled = false;
          })
          .catch(function (err) {
            if (txt) txt.textContent = original;
            if (submitBtn) submitBtn.disabled = false;
            window.alert((err && err.description) || strings.cartError || 'Something went wrong.');
          });
      });

      updateVariant();
    });
  }

  /* =======================================================================
     GENERIC AJAX "add" buttons (upsell / quick add)
     ===================================================================== */
  function initQuickAdd() {
    $all('[data-quick-add]').forEach(function (btn) {
      on(btn, 'click', function (e) {
        e.preventDefault();
        var id = btn.getAttribute('data-variant-id');
        if (!id) return;
        btn.disabled = true;
        Cart.add([{ id: parseInt(id, 10), quantity: 1 }])
          .then(function () { btn.disabled = false; if (Morena.cartDrawer) Morena.cartDrawer.open(); })
          .catch(function () { btn.disabled = false; });
      });
    });
  }

  /* =======================================================================
     PRODUCT GALLERY — mobile dots + zoom lightbox
     ===================================================================== */
  function initGallery() {
    var gallery = $('[data-gallery]');
    if (!gallery) return;
    var viewport = $('[data-gallery-viewport]', gallery);
    var dots = $all('[data-gallery-dot]', gallery);

    if (viewport && dots.length) {
      var slides = $all('[data-gallery-slide]', viewport);
      on(viewport, 'scroll', function () {
        var idx = Math.round(viewport.scrollLeft / viewport.clientWidth);
        dots.forEach(function (d, i) { d.setAttribute('aria-current', i === idx ? 'true' : 'false'); });
      }, { passive: true });
      dots.forEach(function (dot, i) {
        on(dot, 'click', function () { if (slides[i]) slides[i].scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' }); });
      });
    }

    // Zoom lightbox
    var lightbox = $('[data-zoom-lightbox]');
    var lightboxImg = lightbox ? $('img', lightbox) : null;
    if (lightbox && lightboxImg) {
      $all('[data-zoom]', gallery).forEach(function (img) {
        on(img, 'click', function () {
          lightboxImg.src = img.getAttribute('data-zoom-src') || img.src;
          lightboxImg.alt = img.alt || '';
          lightbox.setAttribute('data-open', 'true');
          lockScroll(true);
        });
      });
      function closeZoom() { lightbox.setAttribute('data-open', 'false'); lockScroll(false); }
      on(lightbox, 'click', closeZoom);
      on(document, 'keydown', function (e) { if (e.key === 'Escape' && lightbox.getAttribute('data-open') === 'true') closeZoom(); });
    }
  }

  /* =======================================================================
     STICKY ADD-TO-CART — show when main ATC scrolls out of view
     ===================================================================== */
  function initStickyAtc() {
    var sticky = $('[data-sticky-atc]');
    var anchor = $('[data-atc-anchor]');
    if (!sticky || !anchor || !('IntersectionObserver' in window)) return;
    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        sticky.setAttribute('data-visible', entry.isIntersecting ? 'false' : 'true');
      });
    }, { rootMargin: '0px 0px -10% 0px' });
    observer.observe(anchor);

    // proxy sticky button to the main form submit
    var stickyBtn = $('[data-sticky-atc-btn]', sticky);
    on(stickyBtn, 'click', function () {
      var form = $('[data-product-form]');
      if (form) form.requestSubmit ? form.requestSubmit() : form.querySelector('[data-atc]').click();
    });
  }

  /* =======================================================================
     PRODUCT RECOMMENDATIONS — load via Shopify Recommendations API
     ===================================================================== */
  function initRecommendations() {
    var el = $('[data-recommendations]');
    if (!el) return;
    var url = el.getAttribute('data-url');
    if (!url) return;
    fetch(url)
      .then(function (r) { return r.text(); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var fresh = doc.querySelector('[data-recommendations]');
        if (fresh && fresh.innerHTML.trim()) {
          el.innerHTML = fresh.innerHTML;
        }
      })
      .catch(function () { /* silent — section just stays empty */ });
  }

  /* =======================================================================
     SCROLL REVEAL — IntersectionObserver, progressive enhancement
     Elements with [data-reveal] (or .clip) fade/clip into view once.
     ===================================================================== */
  function initReveal() {
    var nodes = $all('[data-reveal], .clip');
    if (!nodes.length) return;

    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce || !('IntersectionObserver' in window)) {
      nodes.forEach(function (n) { n.classList.add('is-visible'); });
      return;
    }

    // Optional stagger: a container with [data-reveal-group] delays its children.
    $all('[data-reveal-group]').forEach(function (group) {
      var step = parseFloat(group.getAttribute('data-reveal-group')) || 0.08;
      $all('[data-reveal], .clip', group).forEach(function (child, i) {
        child.style.setProperty('--reveal-delay', (i * step) + 's');
      });
    });

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          io.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.12 });

    nodes.forEach(function (n) { io.observe(n); });
  }

  /* =======================================================================
     MARQUEE — guarantee a seamless loop by duplicating the track content.
     Markup ships one [data-marquee-track]; we clone it so translateX(-50%)
     loops without a gap regardless of how much content the merchant entered.
     ===================================================================== */
  function initMarquee() {
    $all('[data-marquee-track]').forEach(function (track) {
      if (track.getAttribute('data-cloned') === 'true') return;
      var clone = track.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      clone.removeAttribute('data-marquee-track');
      track.parentNode.appendChild(clone);
      track.setAttribute('data-cloned', 'true');
    });
  }

  /* =======================================================================
     ACCORDIONS (progressive — also works without Alpine)
     ===================================================================== */
  function initAccordions() {
    $all('[data-accordion-trigger]').forEach(function (trigger) {
      var panel = document.getElementById(trigger.getAttribute('aria-controls'));
      on(trigger, 'click', function () {
        var open = trigger.getAttribute('aria-expanded') === 'true';
        trigger.setAttribute('aria-expanded', String(!open));
        if (panel) panel.hidden = open;
      });
    });
  }

  /* =======================================================================
     CAMPAIGN COUNTDOWN — merchant edits the end date/time in the section
     settings (plain text, no code). Ticks every second; shows an "ended"
     state and hides the grid once the target date passes.
     ===================================================================== */
  function initCountdown() {
    $all('[data-countdown]').forEach(function (el) {
      var target = new Date(el.getAttribute('data-end'));
      var grid = $('[data-countdown-grid]', el);
      var endedEl = $('[data-countdown-ended]', el);
      if (isNaN(target.getTime())) return;

      var daysEl = $('[data-cd-days]', el);
      var hoursEl = $('[data-cd-hours]', el);
      var minsEl = $('[data-cd-mins]', el);
      var secsEl = $('[data-cd-secs]', el);

      function pad(n) { return String(n).length < 2 ? '0' + n : String(n); }

      function tick() {
        var diff = target.getTime() - Date.now();
        if (diff <= 0) {
          if (grid) grid.hidden = true;
          if (endedEl) endedEl.hidden = false;
          clearInterval(timer);
          return;
        }
        var days = Math.floor(diff / 86400000);
        var hours = Math.floor((diff % 86400000) / 3600000);
        var mins = Math.floor((diff % 3600000) / 60000);
        var secs = Math.floor((diff % 60000) / 1000);
        if (daysEl) daysEl.textContent = days;
        if (hoursEl) hoursEl.textContent = pad(hours);
        if (minsEl) minsEl.textContent = pad(mins);
        if (secsEl) secsEl.textContent = pad(secs);
      }

      tick();
      var timer = setInterval(tick, 1000);
    });
  }

  /* =======================================================================
     NEWSLETTER inline success message handled by Shopify; nothing extra.
     ===================================================================== */

  /* ----- BOOT ------------------------------------------------------------ */
  function init() {
    initCartDrawer();
    initNavDrawer();
    initProductForms();
    initQuickAdd();
    initGallery();
    initStickyAtc();
    initRecommendations();
    initAccordions();
    initCountdown();
    initReveal();
    initMarquee();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
